import type { GetServerSidePropsResult } from 'next';
import { useSelector } from 'react-redux';
import { SimpleHead } from 'shared/page/Head';
import { Main } from 'shared/page/Main';
import { getPageCounter } from 'shared/prometheus/prometheus';
import { fetchHotel, fetchOffersForHotel } from '@/api/fetcher';
import { HotelPageLayout } from '@/components/hotel/HotelPageLayout';
import { feConfig } from '@/config';
import { useHotelSeo } from '@/hooks/useHotelSeo';
import type { Filters, HotelResults, PaleState } from '@/interfaces';
import { browserQueryParamsToFilters, filtersToApiQueryParams } from '@/lib/utils/queryParamFilterConversions';
import { runAllMiddlewares } from '@/server/middleware';
import { actions } from '@/state/filterActions';
import { actions as hotelActions } from '@/state/hotelActions';
import { actions as hotelResultActions } from '@/state/hotelResultActions';
import { wrapper } from '@/state/store';

const getPreferredSupplier = (hotelResults: HotelResults): string | undefined =>
    hotelResults.offers.length > 0 ? hotelResults.offers[0].supplier : undefined;

const HotelPage = (props) => {
    wrapper.useHydration(props);
    const hotel = useSelector((state: PaleState) => state.hotel);

    const { title, description, url, image } = useHotelSeo(hotel);

    return (
        <>
            <SimpleHead title={title} description={description} canonical={url} imageUrl={image} />
            <Main broadcastUrl="/broadcasts?path=www.finn.no/reise/pakkereiser">
                <HotelPageLayout />
            </Main>
        </>
    );
};

export const getServerSideProps = wrapper.getServerSideProps(
    (store) =>
        // biome-ignore lint/suspicious/noExplicitAny: This was set before biome was added
        async ({ req, res, query }): Promise<GetServerSidePropsResult<any>> => {
            const props = await runAllMiddlewares(req, res, query);

            const filters: Partial<Filters> = browserQueryParamsToFilters(query);
            store.dispatch(actions.filter(filters));

            const apiQueryParams = filtersToApiQueryParams(filters);
            const { statusCode: offerStatusCode, data: offerData } = await fetchOffersForHotel(apiQueryParams);
            const preferredSupplier = offerStatusCode === 200 ? getPreferredSupplier(offerData as HotelResults) : undefined;
            const { statusCode: hotelStatusCode, data: hotelData } = await fetchHotel({
                ...apiQueryParams,
                preferredSupplier,
            });

            if (hotelStatusCode === 200) {
                store.dispatch(hotelActions.setHotel(hotelData));
            }

            if (offerStatusCode === 200) {
                store.dispatch(hotelResultActions.setResults(offerData));
            } else {
                store.dispatch(hotelResultActions.setError());
            }
            getPageCounter().inc({ app: 'pale-pp-web', page: 'hotell' });
            return { props: { ...props, config: feConfig } };
        },
);

export default HotelPage;
